// nono-header.js: progressive enhancement for the shell header (00 §02) and mobile menu (00 §03).
// Everything works without it: dropdowns and menu groups are <details>, the menu button is a link to #nono-meny.
// Adds: --header-height on :root (measured), one dropdown open at a time, Escape / click outside / focus leaving
// closes a dropdown (focus back to its toggle on Escape), and the mobile menu as a modal <dialog> (focus to «Lukk
// meny», Esc closes, focus returns to the menu button, page scroll locked).

const d = document;
const root = d.documentElement;
const header = d.querySelector('[data-shell-header]');

if (header) {
  root.classList.add('nono-header-js');
  const menu = d.getElementById('nono-meny');
  const opener = header.querySelector('[data-menu-open]');
  const disclosures = [...header.querySelectorAll('[data-nav-disclosure]')];
  const panels = disclosures.filter((el) => el.dataset.navDisclosure === 'panel');
  const canModal = !!(menu && opener && menu.showModal);

  const closeMenu = () => {
    if (menu && menu.open) menu.close();
  };

  // Header height for scroll-padding and any consumer; closes the menu when the header turns into the desktop layout.
  const measure = () => {
    root.style.setProperty('--header-height', `${header.offsetHeight}px`);
    if (canModal && menu.open && !opener.offsetParent) closeMenu();
  };
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  measure();

  // Smart sticky (setting «Når du ruller» = scroll_up, owner 19.10.2026): hidden while the reader scrolls down,
  // back on the first small scroll up and always at the top of the page. Never hidden while a dropdown or the
  // menu is open or keyboard focus is inside the header. 8 px of travel either way before it changes (no flicker).
  if (header.dataset.sticky === 'scroll_up') {
    const wrap = header.closest('.shopify-section') || header;
    const STEP = 8;
    let anchor = scrollY;
    let queued = 0;
    const busy = () =>
      root.classList.contains('nono-menu-open') ||
      header.matches(':focus-within') ||
      [...header.querySelectorAll('[data-nav-disclosure="panel"]')].some((p) => p.open);
    const show = () => header.removeAttribute('data-hidden');
    const update = () => {
      queued = 0;
      const y = scrollY;
      // Still inside the header's own place in the page (the sections above it + header): always shown. A stuck
      // element's offsetTop follows the scroll, so the place is summed from the sections before it.
      let top = 0;
      for (let s = wrap.previousElementSibling; s; s = s.previousElementSibling) if (s.classList.contains('shopify-section')) top += s.offsetHeight;
      if (y <= top + header.offsetHeight) { show(); anchor = y; return; }
      if (y - anchor > STEP) { if (!busy()) header.setAttribute('data-hidden', ''); anchor = y; }
      else if (anchor - y > STEP) { show(); anchor = y; }
    };
    addEventListener('scroll', () => { if (!queued) queued = requestAnimationFrame(update); }, { passive: true });
    header.addEventListener('focusin', show);
    header.addEventListener('toggle', show, true);
  }

  // One open at a time per family (panel | group); `name` does this natively in newer browsers.
  for (const el of disclosures) {
    el.addEventListener('toggle', () => {
      if (!el.open) return;
      for (const other of disclosures) {
        if (other !== el && other.open && other.dataset.navDisclosure === el.dataset.navDisclosure) other.open = false;
      }
    });
  }

  // Hover-to-open (owner 19.10.2026): with a mouse, a dropdown opens after 0.15 s of resting on it and closes 0.3 s
  // after the pointer has left both the item and its panel (no flicker on a quick pass). Click, Enter and Space work
  // as before; a click right after a hover-open keeps the panel open instead of toggling it shut. Touch is untouched.
  const fineHover = matchMedia('(hover: hover) and (pointer: fine)');
  for (const panel of panels) {
    let openTimer = 0;
    let closeTimer = 0;
    const toggle = panel.querySelector('summary');
    panel.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !fineHover.matches) return;
      clearTimeout(closeTimer);
      if (panel.open) return;
      openTimer = setTimeout(() => {
        panel.dataset.hoverOpen = '1';
        panel.open = true;
      }, 150);
    });
    panel.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(() => {
        // Keyboard focus inside keeps it open; the focusout handler below closes it when focus leaves.
        if (panel.querySelector(':focus-visible')) return;
        panel.open = false;
      }, 300);
    });
    toggle?.addEventListener('click', (e) => {
      if (panel.dataset.hoverOpen === '1' && panel.open) e.preventDefault();
      delete panel.dataset.hoverOpen;
    });
    panel.addEventListener('toggle', () => {
      if (!panel.open) delete panel.dataset.hoverOpen;
    });
  }

  for (const panel of panels) {
    panel.addEventListener('focusout', (e) => {
      if (panel.open && e.relatedTarget && !panel.contains(e.relatedTarget)) panel.open = false;
    });
  }

  d.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    for (const panel of panels) {
      if (!panel.open) continue;
      const inside = panel.contains(d.activeElement);
      panel.open = false;
      if (inside) panel.querySelector('summary').focus();
      e.preventDefault();
    }
  });

  d.addEventListener('click', (e) => {
    for (const panel of panels) if (panel.open && !panel.contains(e.target)) panel.open = false;
  });

  if (canModal) {
    const closers = [...menu.querySelectorAll('[data-menu-close]')];

    // Links that act as buttons once JS runs: name the role and let Space activate them.
    for (const el of [opener, ...closers]) {
      el.setAttribute('role', 'button');
      el.addEventListener('keydown', (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          el.click();
        }
      });
    }
    opener.setAttribute('aria-expanded', 'false');

    const openMenu = () => {
      if (menu.open) return;
      menu.showModal();
      root.classList.add('nono-menu-open');
      opener.setAttribute('aria-expanded', 'true');
      (closers[0] || menu).focus();
    };

    opener.addEventListener('click', (e) => {
      if (e.button || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openMenu();
    });

    for (const c of closers) {
      c.addEventListener('click', (e) => {
        e.preventDefault();
        closeMenu();
      });
    }

    menu.addEventListener('close', () => {
      root.classList.remove('nono-menu-open');
      opener.setAttribute('aria-expanded', 'false');
      if (opener.offsetParent) opener.focus();
    });

    // A same-page anchor inside the menu: close first so the target is visible.
    menu.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]:not([data-menu-close])');
      if (a && !a.hasAttribute('data-booking-trigger')) closeMenu();
    });

    // Arrived with #nono-meny (no-JS link shared or reloaded): upgrade to the modal and drop the hash.
    if (location.hash === '#nono-meny') {
      history.replaceState(null, '', location.pathname + location.search);
      if (opener.offsetParent) openMenu();
    }
  }
}
